/**
 * Callout — the rendered form of a GFM alert blockquote (#930).
 *
 * Kind → tone mapping follows the design-tokens decision tree, not GitHub's
 * palette:
 *   NOTE    → cat-sky      (a kind, scannable — informational)
 *   TIP     → status-ok    (a healthy state — "this helps")
 *   WARNING → status-warn  (waiting / needs attention)
 *   CAUTION → status-danger(something can go wrong)
 *
 * All colour lives in `index.css` (`.chat-callout-*`) against the chip token
 * triples, which are declared under BOTH `:root` and `.dark` — no `dark:`
 * variants here, per design-grammar §3. Body text stays `--stevens-ink`; only
 * the label row wears the family fg.
 */
import type { ReactNode } from 'react';
import { Info, Lightbulb, TriangleAlert, OctagonAlert, type LucideIcon } from 'lucide-react';
import type { CalloutKind } from './remarkCallouts';

const META: Record<CalloutKind, { label: string; Icon: LucideIcon }> = {
  note: { label: 'Note', Icon: Info },
  tip: { label: 'Tip', Icon: Lightbulb },
  warning: { label: 'Warning', Icon: TriangleAlert },
  caution: { label: 'Caution', Icon: OctagonAlert },
};

export function Callout({ kind, children }: { kind: CalloutKind; children: ReactNode }) {
  const { label, Icon } = META[kind];
  return (
    <div className={`chat-callout chat-callout-${kind}`} role="note">
      <div className="chat-callout-label">
        <Icon size={15} aria-hidden="true" />
        <span>{label}</span>
      </div>
      <div className="chat-callout-body">{children}</div>
    </div>
  );
}
